import React, { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Popover, Card, Button, Row, Col, Form, Select, Spin, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { EditOutlined, PlusSquareOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { ApplicationState } from '../../../reducers';
import { AttributeRankedValue, AttributeTranslation, Unit } from '../../../../types/attributes';
import AttributeTranslationTable from './AttributeTranslationTable';
import { fetchUnits, fetchValues } from '../../../actions/items/attributes/fetch';
import { AsyncDispatch } from '../../../../types/global';
import { generateUUID, typingDone } from '../../../utils/Utils';
import { hasPermission } from '../../../utils/Permissions';
import { fetchTranslations } from '../../../actions/parent/language/fetch';
import { LanguageTranslation } from '../../../../types/language';
import { useAppDispatch } from '../../../store/ReduxStore';

type FormValues = {
  valueId: number | null;
  uomId: number | null;
  uomlabel: string;
  valueName: string;
  valueUniqueId?: string;
};

type AttributeValuePopoverProps = {
  children?: React.ReactNode;
  type?: string;
  valueName?: string;
  attrMetaUomLabel?: string;
  valueId?: number;
  metaUomId?: number | null;
  uniqueId?: string;
  translations?: AttributeTranslation[];
  unitTranslations?: AttributeTranslation[];
  attributeIds?: number[];
  addButtonVisisble?: boolean;
  handleChange: (props: {
    uomlabel: string;
    valueId: number | null;
    uniqueId?: string;
    prevValueId?: number;
    uomId: number | null;
    valueName: string;
  }) => void;
  onClose?: () => void;
};

const AttributeValuePopover: React.FC<AttributeValuePopoverProps> = props => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();
  const navigate = useNavigate();

  const [valueTranslations, setValueTranslations] = React.useState(props.translations);
  const [unitTranslations, setUnitTranslations] = React.useState(props.unitTranslations);

  const {
    selectedItemIds,
    allSelectedItemIds,
    units,
    attributeValues,
    fetchingValues,
    fetchingUnits,
    user,
  } = useSelector((state: ApplicationState) => {
    return {
      selectedItemIds: state.catalogue.catalogue.selectedItemIds,
      allSelectedItemIds: state.catalogue.catalogue.allSelectedItemIds,
      units: state.items.attributes.rankedUnits,
      attributeValues: state.items.attributes.rankedValues,
      fetchingValues: state.items.attributes.fetchingValues,
      fetchingUnits: state.items.attributes.fetchingUnits,
      user: state.user.user,
    };
  }, shallowEqual);

  const [popoverVisible, setPopoverVisible] = React.useState(false);
  const [keywords, setKeywords] = React.useState('');
  const [valuesList, setValuesList] = React.useState<AttributeRankedValue[]>(attributeValues || []);

  const selectedBrandItemIds = useMemo(
    () => (selectedItemIds.length > 1 ? allSelectedItemIds : selectedItemIds),
    [allSelectedItemIds, selectedItemIds]
  );

  const canManageTranslations = hasPermission(user, 'can_manage_translation');

  const handleFetchAttrValueTranslation = (value: string) => {
    dispatch(fetchTranslations({ translationTypeId: 2, keywords: value })).then(response => {
      const translation = response.value.data.find((data: LanguageTranslation) =>
        data.translations?.find(t => t.language_id === 1 && t.value === value)
      );
      setValueTranslations(translation?.translations || []);
    });
  };

  const handleFetchAttrUnitTranslation = (value: string) => {
    dispatch(fetchTranslations({ translationTypeId: 3, keywords: value })).then(response => {
      const translation = response.value.data.find((data: LanguageTranslation) =>
        data.translations?.find(t => t.language_id === 1 && t.value === value)
      );
      setUnitTranslations(translation?.translations || []);
    });
  };

  const fetchValuesAndUnits = () => {
    dispatch(fetchUnits({ itemIds: selectedBrandItemIds, attributeIds: props.attributeIds }));
    dispatch(
      // @ts-ignore
      fetchValues({ itemIds: selectedBrandItemIds, attributeIds: props.attributeIds })
    ).then(response => {
      setValuesList(response.action.payload.data);
    });
  };

  const getAtrrValues = (keywords: string) => {
    setValuesList([]);
    dispatch(
      // @ts-ignore
      fetchValues({
        itemIds: selectedBrandItemIds,
        attributeIds: props.attributeIds,
        filter: keywords,
      })
    ).then(response => {
      setValuesList(response.action.payload.data);
      const findValue = response.action.payload.data.find(
        (v: AttributeRankedValue) => v.name === keywords
      );
      if (!findValue)
        setValuesList([
          ...(keywords ? [{ name: keywords, id: null, uniqueId: generateUUID() }] : []),
          ...response.action.payload.data,
        ]);
    });
  };

  const getUnits = (unit: string) => {
    dispatch(
      fetchUnits({
        itemIds: selectedBrandItemIds,
        attributeIds: props.attributeIds,
        filter: unit,
      })
    );
  };

  const validationSchema = Yup.object().shape({
    valueId: Yup.number()
      .nullable()
      .when(['valueName'], ([valueName], schema) => {
        return !valueName || valueName.length === 0
          ? schema.required(t('validation:required'))
          : schema;
      }),
  });

  const formMethods = useForm<FormValues>({
    defaultValues: {
      valueId: props.valueId || null,
      uomId: props.metaUomId || null,
      uomlabel: props.attrMetaUomLabel || '',
      valueName: props.valueName || '',
      valueUniqueId: valuesList.find(v => v.name === props.valueName)?.uniqueId,
    },
    resolver: yupResolver(validationSchema) as any,
    mode: 'onTouched',
  });

  const { handleSubmit, setValue, getValues, reset } = formMethods;

  React.useEffect(() => {
    if (!popoverVisible) {
      reset({
        valueId: props.valueId || null,
        uomId: props.metaUomId || null,
        uomlabel: props.attrMetaUomLabel || '',
        valueName: props.valueName || '',
        valueUniqueId: valuesList.find(v => v.name === props.valueName)?.uniqueId,
      });
    }
  }, [popoverVisible, props, reset, valuesList]);

  const onSubmit = (values: FormValues) => {
    props.handleChange({
      uomlabel: values.uomlabel,
      valueId: values.valueId,
      uomId: values.uomId,
      valueName: values.valueName,
      prevValueId: props.valueId,
      uniqueId: props.uniqueId || values.valueUniqueId,
    });
    if (props.onClose) props.onClose();
    setPopoverVisible(false);
  };

  return (
    <Popover
      key={props.valueId || -1}
      open={popoverVisible}
      onOpenChange={visible => {
        if (!visible && props.onClose) props.onClose();
        setPopoverVisible(visible);
        if (visible) fetchValuesAndUnits();
      }}
      overlayClassName="help attribute_popover"
      placement="bottomLeft"
      destroyTooltipOnHide
      content={
        popoverVisible ? (
          <Card className="attribute__form" onClick={e => e.stopPropagation()} bordered={false}>
            <FormProvider {...formMethods}>
              <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
                <Row gutter={20} align="top">
                  <Col span={12}>
                    <Controller
                      name="valueId"
                      control={formMethods.control}
                      render={({ field, fieldState: { error } }) => {
                        const currentValues = getValues();
                        return (
                          <Form.Item
                            label={t('attributes:value')}
                            required
                            data-testid="title"
                            validateStatus={error ? 'error' : ''}
                            help={error?.message || undefined}
                          >
                            <Select
                              {...field}
                              data-testid="value"
                              showSearch
                              value={
                                valuesList.find(
                                  v =>
                                    v.id === currentValues.valueId ||
                                    (currentValues.valueUniqueId &&
                                      v.uniqueId === currentValues.valueUniqueId)
                                )
                                  ? currentValues.valueId || currentValues.valueUniqueId
                                  : currentValues.valueName
                              }
                              onClick={e => e.stopPropagation()}
                              placeholder={t('attributes:selectValue')}
                              notFoundContent={fetchingValues ? <Spin size="small" /> : undefined}
                              filterOption={false}
                              onSearch={keywords => {
                                setKeywords(keywords);
                                typingDone(() => getAtrrValues(keywords));
                              }}
                              onChange={valueId => {
                                const value = valuesList.find(
                                  v => v.id === valueId || v.uniqueId === valueId
                                );
                                setValue('valueName', value?.name || '');
                                setValue('valueId', typeof valueId === 'string' ? null : valueId);
                                setValue('valueUniqueId', value?.uniqueId);
                                if (valueId) handleFetchAttrValueTranslation(value?.name || '');
                                else setValueTranslations([]);
                              }}
                              style={{ width: '100%' }}
                              onDropdownVisibleChange={open => {
                                if (!open && !getValues('valueName')) {
                                  const value = valuesList.find(v => v.name === keywords);
                                  setValue('valueName', value?.name || keywords);
                                  setValue('valueUniqueId', value?.uniqueId);
                                }
                              }}
                              options={[
                                ...((!fetchingValues &&
                                  currentValues.valueName &&
                                  !valuesList.find(v => v.name === currentValues.valueName) && [
                                    {
                                      key:
                                        currentValues.valueId ||
                                        currentValues.valueUniqueId ||
                                        currentValues.valueName,
                                      value:
                                        currentValues.valueId ||
                                        currentValues.valueUniqueId ||
                                        currentValues.valueName,
                                      label: currentValues.valueName,
                                    },
                                  ]) ||
                                  []),
                                ...valuesList.map(u => ({
                                  key: u.id ? u.id : u.uniqueId,
                                  value: u.id ? u.id : u.uniqueId,
                                  label: u.name,
                                })),
                              ]}
                            />
                          </Form.Item>
                        );
                      }}
                    />
                  </Col>
                  <Col span={12}>
                    <Form.Item label={t('attributes:unit')}>
                      <Select
                        data-testid="unit"
                        showSearch
                        value={getValues('uomId') || undefined}
                        placeholder={t('attributes:selectUnit')}
                        onClick={e => e.stopPropagation()}
                        notFoundContent={fetchingUnits ? <Spin size="small" /> : undefined}
                        filterOption={false}
                        onSearch={(keywords: string) => {
                          const unit = keywords;
                          typingDone(() => getUnits(unit));
                        }}
                        onChange={(uomId: number) => {
                          const uomLabel = units.find((u: Unit) => u.id === uomId)?.label;
                          setValue('uomId', uomId || null);
                          setValue('uomlabel', uomLabel || '');

                          if (uomId) handleFetchAttrUnitTranslation(uomLabel || '');
                          else setUnitTranslations([]);
                        }}
                        style={{ width: '100%' }}
                        allowClear
                        options={units.map((u: Unit) => ({
                          key: u.id,
                          value: u.id,
                          label: <span data-testid="unit-option">{u.description}</span>,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                {canManageTranslations && (
                  <React.Fragment>
                    <div className="flex justify-between">
                      <div>{t('attributes:translations')}</div>
                      <Popconfirm
                        title={t('attributes:confirmSwitchToTanslations')}
                        okText={t('common:yes')}
                        cancelText={t('common:cancel')}
                        onConfirm={() =>
                          navigate('/settings/translations/translation-settings', {
                            state: { segmentId: 2 },
                          })
                        }
                      >
                        <Button size="small">{t('attributes:editTranslations')}</Button>
                      </Popconfirm>
                    </div>
                    <div className="attribute-translation-table">
                      <AttributeTranslationTable
                        type={props.type}
                        translations={valueTranslations || []}
                        unitTranslations={unitTranslations || []}
                      />
                    </div>
                  </React.Fragment>
                )}

                <div className="flex-col items-end mt-2">
                  <div>
                    <Button
                      onClick={e => {
                        if (props.onClose) props.onClose();
                        setPopoverVisible(false);
                        e.stopPropagation();
                      }}
                      className="drawer-submit__bottom-cancel"
                    >
                      {t('common:cancel')}
                    </Button>
                    <Button data-testid="ok" type="primary" htmlType="submit">
                      {t('common:ok')}
                    </Button>
                  </div>
                </div>
              </Form>
            </FormProvider>
          </Card>
        ) : null
      }
      trigger="click"
    >
      {props.type === 'addValue' && (
        <div className="attribute__add-value-wrapper">
          <PlusSquareOutlined
            className={classNames('attribute__add-value', {
              visible: popoverVisible || props.addButtonVisisble,
            })}
            onClick={e => {
              setPopoverVisible(true);
              fetchValuesAndUnits();
              e.stopPropagation();
            }}
          />
        </div>
      )}
      {props.type === 'addValueButton' && (
        <Button
          onClick={() => {
            setPopoverVisible(true);
          }}
          type="primary"
          ghost
          size="small"
          data-testid="add-value"
        >
          {t('attributes:addValue')}
        </Button>
      )}
      {props.type === 'editValue' && (
        <EditOutlined
          onClick={e => {
            setPopoverVisible(true);
            fetchValuesAndUnits();
            e.stopPropagation();
          }}
        />
      )}
      {props.type === 'drawerEditValue' && (
        <EditOutlined
          onClick={e => {
            setPopoverVisible(true);
            e.stopPropagation();
          }}
        />
      )}
      {!props.type && props.children}
    </Popover>
  );
};

export default AttributeValuePopover;
